import { useEffect, useState } from 'react';
import { api, UnauthorizedError, type ServiceKey } from '../api';

type Props = {
  onClose: () => void;
  onSessionExpired: () => void;
};

export function ServiceKeys({ onClose, onSessionExpired }: Props) {
  const [keys, setKeys] = useState<ServiceKey[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealed, setRevealed] = useState<{ id: string; name: string; key: string } | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    api
      .listKeys()
      .then((r) => setKeys(r.data))
      .catch((e) => {
        if (e instanceof UnauthorizedError) onSessionExpired();
        else setError(String(e.message || e));
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    setError(null);
    try {
      const r = await api.createKey(name);
      setRevealed({ id: r.data.id, name: r.data.name, key: r.key });
      setNewName('');
      setKeys((ks) => [r.data, ...ks]);
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e.message || e));
    } finally {
      setCreating(false);
    }
  };

  const onDelete = async (id: string, name: string) => {
    if (!confirm(`Revogar a chave "${name}"? Aplicações usando esta chave perderão acesso.`)) return;
    try {
      await api.deleteKey(id);
      setKeys((ks) => ks.filter((k) => k.id !== id));
      if (revealed?.id === id) setRevealed(null);
    } catch (e: any) {
      if (e instanceof UnauthorizedError) onSessionExpired();
      else setError(String(e.message || e));
    }
  };

  return (
    <div className="modal-backdrop" onClick={onClose} data-testid="keys-modal">
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <header className="modal-head">
          <div className="modal-title">Service keys</div>
          <button className="btn" onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </header>

        <p className="modal-sub">
          Tokens de longa duração para autenticar o backend via MCP ou outras automações em produção.
          Envie como header <code>Authorization: Bearer &lt;key&gt;</code>.
        </p>

        <form className="key-form" onSubmit={onCreate}>
          <input
            placeholder="Nome (ex.: MCP do Claude — laptop)"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            maxLength={80}
            data-testid="key-name"
          />
          <button
            className="btn primary"
            type="submit"
            disabled={creating || !newName.trim()}
            data-testid="key-create"
          >
            {creating ? 'Criando…' : 'Criar chave'}
          </button>
        </form>

        {error && <div className="error" data-testid="keys-error">{error}</div>}

        {revealed && (
          <div className="key-reveal" data-testid="key-reveal">
            <div className="key-reveal-title">Chave criada: {revealed.name}</div>
            <div className="key-reveal-sub">
              Copie agora — esta é a única vez que ela aparece. Depois, só o prefixo fica visível.
            </div>
            <div className="key-reveal-row">
              <code className="key-reveal-value">{revealed.key}</code>
              <button
                className="btn"
                onClick={() => navigator.clipboard?.writeText(revealed.key)}
                data-testid="key-copy"
              >
                Copiar
              </button>
              <button className="btn" onClick={() => setRevealed(null)}>
                Fechar
              </button>
            </div>
          </div>
        )}

        <div className="key-list" data-testid="key-list">
          {loading && <div className="muted">carregando…</div>}
          {!loading && keys.length === 0 && (
            <div className="muted">Nenhuma chave ativa. Crie uma acima.</div>
          )}
          {!loading &&
            keys.map((k) => (
              <div key={k.id} className="key-row" data-testid={`key-row-${k.id}`}>
                <div className="key-meta">
                  <div className="key-name">{k.name}</div>
                  <div className="key-sub">
                    <code>{k.prefix}…</code>
                    <span>·</span>
                    <span>criada {formatTs(k.created_at)}</span>
                    <span>·</span>
                    <span>último uso {k.last_used_at ? formatTs(k.last_used_at) : 'nunca'}</span>
                  </div>
                </div>
                <button
                  className="btn danger"
                  onClick={() => onDelete(k.id, k.name)}
                  data-testid={`key-delete-${k.id}`}
                >
                  Revogar
                </button>
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}

function formatTs(ts: number): string {
  const d = new Date(ts * 1000);
  return d.toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}
